import { z } from 'zod';

const ArticleContentNodeTypeSchema = z.enum([
    'paragraph',
    'heading',
    'bulletList',
    'orderedList',
    'listItem',
    'blockquote',
    'codeBlock',
    'image',
    'text',
    'hardBreak',
    'referenceList',
    'referenceItem',
]);

const ArticleContentMarkTypeSchema = z.enum([
    'bold',
    'italic',
    'strike',
    'code',
    'link',
    'superscript',
]);

export type ArticleContentMark = {
    type: z.infer<typeof ArticleContentMarkTypeSchema>;
    attrs?: Record<string, unknown>;
};

export type ArticleContentNode = {
    type: z.infer<typeof ArticleContentNodeTypeSchema>;
    attrs?: Record<string, unknown>;
    marks?: ArticleContentMark[];
    text?: string;
    content?: ArticleContentNode[];
};

export type ArticleContent = {
    type: 'doc';
    content: ArticleContentNode[];
};

const ArticleContentMarkSchema: z.ZodType<ArticleContentMark> = z
    .object({
        type: ArticleContentMarkTypeSchema,
        attrs: z.record(z.unknown()).optional(),
    })
    .superRefine((mark, ctx) => {
        if (mark.type === 'link') {
            const href = (mark.attrs as Record<string, unknown> | undefined)
                ?.href;
            if (typeof href !== 'string' || href.trim().length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '`link` marks must include attrs.href',
                    path: ['attrs', 'href'],
                });
            }
        }
    });

const ArticleContentNodeSchema: z.ZodType<ArticleContentNode> = z.lazy(() =>
    z
        .object({
            type: ArticleContentNodeTypeSchema,
            attrs: z.record(z.unknown()).optional(),
            marks: z.array(ArticleContentMarkSchema).optional(),
            text: z.string().optional(),
            content: z.array(ArticleContentNodeSchema).optional(),
        })
        .superRefine((node, ctx) => {
            if (node.type === 'text' && node.text === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '`text` nodes must include a `text` value',
                    path: ['text'],
                });
            }

            if (node.type !== 'text' && node.text !== undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '`text` is only allowed on `text` nodes',
                    path: ['text'],
                });
            }

            if (node.type === 'image') {
                const src = (node.attrs as Record<string, unknown> | undefined)
                    ?.src;
                if (typeof src !== 'string' || src.trim().length === 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: '`image` nodes must include attrs.src',
                        path: ['attrs', 'src'],
                    });
                    return;
                }

                if (
                    !(src.startsWith('https://') || src.startsWith('http://'))
                ) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: '`image` attrs.src must be an absolute URL',
                        path: ['attrs', 'src'],
                    });
                }
            }
        }),
);

export const ArticleContentSchema: z.ZodType<ArticleContent> = z.object({
    type: z.literal('doc'),
    content: z.array(ArticleContentNodeSchema),
});
